import React, { useEffect, useRef, useState } from "react";
import { fabric } from "fabric";

const JsonToImage = ({ json, setShowModal, handleImageChange }) => {
  const canvasRef = useRef(null);
  const [imgPreview, setImgPreview] = useState(null);

  useEffect(() => {
    if (!json || !json.editableFields) return;

    const renderCanvasWithFonts = async () => {
      const fields = Object.values(json.editableFields);

      const dataUrlToBlob = (base64) => {
        const parts = base64.split(",");
        const mime = parts[0].match(/:(.*?);/)[1];
        const binary = atob(parts[1]);
        let len = binary.length;
        const u8arr = new Uint8Array(len);
        while (len--) {
          u8arr[len] = binary.charCodeAt(len);
        }
        return new Blob([u8arr], { type: mime });
      };

      const dataUrlToFile = (base64, filename) => {
        const blob = dataUrlToBlob(base64);
        return new File([blob], filename, { type: blob.type });
      };

      const fontFamilies = [
        ...new Set(fields.map((el) => el.font).filter(Boolean)),
      ];

      // Inject Google Fonts
      fontFamilies.forEach((font) => {
        const formatted = font.trim().replace(/\s+/g, "+");
        const linkId = `font-${formatted}`;
        if (!document.getElementById(linkId)) {
          const link = document.createElement("link");
          link.id = linkId;
          link.rel = "stylesheet";
          link.href = `https://fonts.googleapis.com/css2?family=${formatted}&display=swap`;
          document.head.appendChild(link);
        }
      });
      // Wait until all fonts are fully ready
      await document.fonts.ready;

      const canvas = new fabric.StaticCanvas(canvasRef.current, {
        width: json.width || 300,
        height: json.height || 500,
      });

      const bg = json?.editableFields?.background;

      if (bg.type === "image" && bg.value) {
        fabric.Image.fromURL(
          bg.value,
          (img) => {
            if (!img) {
              console.error("Failed to load background image.");
              return;
            }

            const canvasWidth = canvas.getWidth();
            const canvasHeight = canvas.getHeight();

            // Scale image to canvas size (cover or contain logic if needed)
            img.scaleToWidth(canvasWidth);
            img.scaleToHeight(canvasHeight);

            canvas.setBackgroundImage(img, () => {
              renderJsonToCanvas(json, canvas).then(() => {
                const dataURL = canvas.toDataURL({
                  format: "png",
                  quality: 1,
                });
                setImgPreview(dataURL);

                const file = dataUrlToFile(dataURL, "canvas.png");
                handleImageChange(file);
              });
            });
          },
          { crossOrigin: "anonymous" }
        );
      } else {
        const bgColor = bg.type === "color" ? bg.value : "#ffffff";
        canvas.setBackgroundColor(bgColor, () => {
          renderJsonToCanvas(json, canvas).then(() => {
            const dataURL = canvas.toDataURL({
              format: "png",
              quality: 1,
            });
            setImgPreview(dataURL);
            const file = dataUrlToFile(dataURL, "canvas.png");
            handleImageChange(file);
          });
        });
      }
    };

    renderCanvasWithFonts();
  }, [json]);


  const renderJsonToCanvas = async (json, canvas) => {
    const fields = Object.entries(json.editableFields).filter(
      ([key]) => !key.includes("background")
    );

    const promises = fields.map(async ([key, field]) => {
      const {
        type,
        position,
        size,
        filePath,
        text,
        font,
        color,
        scale,
        offset,
        rotation,
        isBold,
        isItalic,
        isUnderline,
        textAlign,
        fontSize,
      } = field;

      // const left = position?.x || 0;
      // const top = position?.y || 0;

      // Calculate position
      let left = 0,
        top = 0;
      let originX = "left",
        originY = "top";

      if (position.x === "center") {
        left = canvas.getWidth() / 2;
        originX = "center";
      } else {
        left = (parseFloat(position.x) / 100) * canvas.getWidth();
      }

      if (position.y === "center") {
        top = canvas.getHeight() / 2;
        originY = "center";
      } else {
        top = (parseFloat(position.y) / 100) * canvas.getHeight();
      }

      // Apply offset if present
      if (offset) {
        left += offset.x || 0;
        top += offset.y || 0;
      }

      if (type === "image") {
        return new Promise((resolve) => {
          fabric.Image.fromURL(
            filePath,
            (img) => {
              if (!img) {
                console.error("Image not loaded for:", filePath);
                resolve();
                return;
              }

              // Scale image to fit
              const desiredWidth =
                size?.width === "100%"
                  ? canvas.getWidth()
                  : parseInt(size?.width) || img.width;

              img.scaleToWidth(desiredWidth);

              if (img) {
                img.set({
                  left,
                  top,
                  // angle: rotation || 0,
                  originX,
                  originY,
                  selectable: false,
                });
              }
              canvas.add(img);
              resolve();
            },
            { crossOrigin: "anonymous" }
          );
        });
      }

      if (type === "text") {
        const textObj = new fabric.Textbox(text || "", {
          left,
          top,

          //         fill: element.style?.color || "black",
          //         fontWeight: element.style?.fontWeight || "normal",
          //         fontStyle: element.style?.fontStyle || "normal",
          //         fontFamily: element.style?.fontFamily || "sans-serif",
          //         textAlign: element.style?.textAlign || "center",
          //         originX,
          //         originY,
          //         selectable: false,

          width:
            size?.width === "100%"
              ? canvas.getWidth()
              : parseInt(size?.width) || 300,
          fontSize: fontSize ? parseInt(fontSize) : 16,
          fontWeight: isBold ? "bold" : "normal",
          fontStyle: isItalic ? "italic" : "normal",
          underline: !!isUnderline,
          fill: color || "#000000",
          fontFamily: font || "sans-serif",
          textAlign: textAlign || "left",
          scaleX: scale || 1,
          scaleY: scale || 1,
          originX,
          originY,
          selectable: false,
        });

        // Add textObj to canvas temporarily
        canvas.add(textObj);

        // Measure required width after applying styles
        const measuredWidth = textObj.calcTextWidth() + 10; // +10 for padding/margin
        const maxCanvasWidth = canvas.getWidth() - (left || 0) - 10; // prevent overflow

        textObj.set({
          width:
            size?.width === "100%"
              ? maxCanvasWidth
              : Math.min(measuredWidth, maxCanvasWidth),
        });

        // 🔥 Force re-layout for height calculation
        textObj.initDimensions();

        // Force render refresh (for font rendering bugs)
        textObj.clone((cloned) => {
          canvas.remove(textObj);
          canvas.add(cloned);
        });
      }
    });

    await Promise.all(promises);
    canvas.renderAll();
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-lg w-[500px] h-[600px] flex flex-col justify-between">
          <h3 className="text-xl font-bold mb-1 text-center bg-gradient-to-br from-blue-950 to-blue-500 py-2 text-white">
            Json Layout Preview
          </h3>
          {/* Top content */}
          <div className="p-4 flex-1 overflow-auto scrollbar-hidden">
            {imgPreview ? (
              <img
                src={imgPreview}
                alt="Preview"
                style={{
                  border: "1px solid #ccc",
                  width: "100%",
                  maxWidth: 400,
                }}
              />
            ) : (
              <p>Rendering preview...</p>
            )}
            <canvas ref={canvasRef} style={{ display: "none" }} />
          </div>

          {/* Footer */}
          <div className="flex justify-end p-4 border-t">
            <button
              onClick={closeModal}
              className="bg-gray-300 text-black w-52 py-2 px-4 rounded-md hover:bg-gray-400 transition duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default JsonToImage;







  // const renderJsonToCanvas = async (json, canvas) => {
  //   const { elements } = json;

  //   // Clear canvas first
  //   canvas.getObjects().forEach((obj) => canvas.remove(obj));

  //   const promises = elements.map(async (element) => {
  //     // Calculate position
  //     let left = 0,
  //       top = 0;
  //     let originX = "left",
  //       originY = "top";

  //     if (element.position.x === "center") {
  //       left = canvas.getWidth() / 2;
  //       originX = "center";
  //     } else {
  //       left = (parseFloat(element.position.x) / 100) * canvas.getWidth();
  //     }

  //     if (element.position.y === "center") {
  //       top = canvas.getHeight() / 2;
  //       originY = "center";
  //     } else {
  //       top = (parseFloat(element.position.y) / 100) * canvas.getHeight();
  //     }

  //     if (element.type === "image" || element.type === "sticker") {
  //       // Case: sticker with filePath (normal image sticker)
  //       if (element.filePath) {
  //         return new Promise((resolve) => {
  //           fabric.Image.fromURL(
  //             element.filePath,
  //             (img) => {
  //               if (!img) {
  //                 console.error("Image not loaded for:", element.filePath);
  //                 resolve();
  //                 return;
  //               }

  //               // Scale image to fit
  //               const desiredWidth =
  //                 element.size?.width === "100%"
  //                   ? canvas.getWidth()
  //                   : parseInt(element.size?.width) || img.width;

  //               img.scaleToWidth(desiredWidth);

  //               img.set({
  //                 left,
  //                 top,
  //                 originX,
  //                 originY,
  //                 selectable: false,
  //               });

  //               canvas.add(img);
  //               resolve();
  //             },
  //             { crossOrigin: "anonymous" }
  //           );
  //         });
  //       } else if (element.type === "sticker") {
  //         // Case: sticker with no filePath — draw rect or circle and text
  //         const width = parseInt(element.size?.width) || 60;
  //         const height = parseInt(element.size?.height) || 60;
  //         const borderRadius =
  //           element.style?.borderRadius === "50%"
  //             ? Math.min(width, height) / 2
  //             : 0;

  //         const shape = new fabric.Rect({
  //           left,
  //           top,
  //           originX,
  //           originY,
  //           width,
  //           height,
  //           rx: borderRadius,
  //           ry: borderRadius,
  //           fill: element.style?.backgroundColor || "#555",
  //           selectable: false,
  //         });

  //         const text = new fabric.Textbox(element.content || "", {
  //           left,
  //           top,
  //           originX: "center",
  //           originY: "center",
  //           width,
  //           fontSize: parseInt(element.style?.fontSize) || 14,
  //           fill: element.style?.color || "white",
  //           fontWeight: element.style?.fontWeight || "bold",
  //           textAlign: element.style?.textAlign || "center",
  //           selectable: false,
  //         });

  //         canvas.add(shape);
  //         canvas.add(text);
  //       }
  //     }

  //     if (
  //       ["text", "promise", "personal-message", "signature"].includes(
  //         element.type
  //       )
  //     ) {
  //       const text = new fabric.Textbox(element.content || "", {
  //         left,
  //         top,
  //         width:
  //           element.size?.width === "100%"
  //             ? canvas.getWidth()
  //             : parseInt(element.size?.width) || 300,
  //         fontSize: element.style?.fontSize
  //           ? parseInt(element.style.fontSize)
  //           : 16,
  //         fill: element.style?.color || "black",
  //         fontWeight: element.style?.fontWeight || "normal",
  //         fontStyle: element.style?.fontStyle || "normal",
  //         fontFamily: element.style?.fontFamily || "sans-serif",
  //         textAlign: element.style?.textAlign || "center",
  //         originX,
  //         originY,
  //         selectable: false,
  //       });
  //       canvas.add(text);

  //       // 🔁 Force font refresh
  //       text.clone((clonedText) => {
  //         canvas.remove(text);
  //         canvas.add(clonedText);
  //       });
  //     }
  //   });

  //   await Promise.all(promises);
  //   canvas.renderAll();
  // };
